import type { VersionRange } from "./versions";

// Control channel messages (docs/PRD.md §7). Compatibility rules: the envelope `{ t, ...fields }`
// and every message's required fields never change; new fields are optional; receivers ignore
// unknown fields and unknown `t` values. New behavior gets a new `t`.

export interface PeerInfo {
  /** Random id for this tab/session. */
  id: string;
  /** Stable id for this browser (persists across sessions). */
  clientId: string;
  name: string;
  /** Catppuccin accent name. */
  color: string;
}

export interface AppInfo {
  /** Semver of the sender's bundle. */
  version: string;
  sha: string;
  /** Origin the bundle was served from (update prompts only trust the same origin). */
  origin: string;
  /** Build time, seconds since epoch. */
  builtAt: number;
}

export interface Hello {
  t: "hello";
  app: AppInfo;
  /** Supported [min, max] version per channel. */
  proto: Record<string, VersionRange>;
  /** Supported [min, max] version per shared-state schema. */
  schema: Record<string, VersionRange>;
  /** Optional capability flags, e.g. "run:python". Never inferred from versions. */
  caps: string[];
  peer: PeerInfo;
  /** Random challenge (base64url) the other side must sign in its `auth`. */
  nonce: string;
}

/** Proves knowledge of the room secret, bound to this connection's DTLS fingerprints. */
export interface Auth {
  t: "auth";
  /** base64url HMAC over (prover fingerprint, verifier fingerprint, verifier nonce). */
  proof: string;
}

export interface AuthOk {
  t: "auth.ok";
}

export interface Ping {
  t: "ping";
  /** Sender's clock, ms. Echoed back in pong. */
  ts: number;
}

export interface Pong {
  t: "pong";
  ts: number;
}

/** The sender changed its display name or color mid-session (additive; older peers ignore it). */
export interface ProfileUpdate {
  t: "profile";
  name: string;
  color: string;
}

export interface Bye {
  t: "bye";
  reason?: string;
}

export type ControlMessage = Hello | Auth | AuthOk | Ping | Pong | ProfileUpdate | Bye;

/** A well-formed envelope whose type this build doesn't know (sent by a newer peer). */
export interface Unknown {
  t: "unknown";
  type: string;
}

/** A known type missing required fields, or not an envelope at all. */
export interface Invalid {
  t: "invalid";
  reason: string;
}

export type Decoded = ControlMessage | Unknown | Invalid;

type Json = Record<string, unknown>;

const isObject = (v: unknown): v is Json =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const isString = (v: unknown): v is string => typeof v === "string";
const isNumber = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

function range(v: unknown): VersionRange | undefined {
  return Array.isArray(v) && v.length >= 2 && isNumber(v[0]) && isNumber(v[1])
    ? [v[0], v[1]]
    : undefined;
}

/** Keeps only well-formed `key: [min, max]` entries. */
function ranges(v: unknown): Record<string, VersionRange> | undefined {
  if (!isObject(v)) return undefined;
  const out: Record<string, VersionRange> = {};
  for (const [key, value] of Object.entries(v)) {
    const r = range(value);
    if (r) out[key] = r;
  }
  return out;
}

function appInfo(v: unknown): AppInfo | undefined {
  if (!isObject(v)) return undefined;
  const { version, sha, origin, builtAt } = v;
  if (!isString(version) || !isString(sha) || !isString(origin) || !isNumber(builtAt)) return;
  return { version, sha, origin, builtAt };
}

function peerInfo(v: unknown): PeerInfo | undefined {
  if (!isObject(v)) return undefined;
  const { id, clientId, name, color } = v;
  if (!isString(id) || !isString(clientId) || !isString(name) || !isString(color)) return;
  return { id, clientId, name, color };
}

const invalid = (reason: string): Invalid => ({ t: "invalid", reason });

/** Decodes one control message from a parsed JSON value. Never throws. */
export function decodeControlValue(value: unknown): Decoded {
  if (!isObject(value) || !isString(value.t)) return invalid("not an envelope");
  const m = value;
  const t: string = value.t;
  switch (t) {
    case "hello": {
      const app = appInfo(m.app);
      const proto = ranges(m.proto);
      const schema = ranges(m.schema);
      const peer = peerInfo(m.peer);
      if (!app || !proto || !schema || !peer || !isString(m.nonce)) return invalid("bad hello");
      const caps = Array.isArray(m.caps) ? m.caps.filter(isString) : [];
      return { t: "hello", app, proto, schema, caps, peer, nonce: m.nonce };
    }
    case "auth":
      return isString(m.proof) ? { t: "auth", proof: m.proof } : invalid("bad auth");
    case "auth.ok":
      return { t: "auth.ok" };
    case "ping":
      return isNumber(m.ts) ? { t: "ping", ts: m.ts } : invalid("bad ping");
    case "pong":
      return isNumber(m.ts) ? { t: "pong", ts: m.ts } : invalid("bad pong");
    case "profile":
      return isString(m.name) && isString(m.color)
        ? { t: "profile", name: m.name, color: m.color }
        : invalid("bad profile");
    case "bye":
      return isString(m.reason) ? { t: "bye", reason: m.reason } : { t: "bye" };
    default:
      return { t: "unknown", type: t };
  }
}

/** Decodes one control message from the wire. Never throws. */
export function decodeControl(raw: string): Decoded {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return invalid("not JSON");
  }
  return decodeControlValue(value);
}

export function encodeControl(message: ControlMessage): string {
  return JSON.stringify(message);
}
